import { Injectable, OnDestroy, computed, signal, effect, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { InsightsService, ExportJob, EnergyPreset } from '@core/services/insights.service';
import { ExportNotificationService } from '@core/services/export-notification.service';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { ExportJobCardState } from '@custom-components';
import {
  buildExportFileName,
  gunzipCsvUtil,
  parseContentDispositionFilename,
} from '@core/utils/gunzip-csv.util';
import { timer, firstValueFrom, Subscription } from 'rxjs';

const AGGRESSIVE_POLL_MS = 2000;
const STANDARD_POLL_MS = 10000;
const LAZY_POLL_MS = 30000;
const MAX_HISTORY = 20;
const MAX_FAILED = 10;

@Injectable({
  providedIn: 'root',
})
export class ExportJobStore implements OnDestroy {
  private readonly insightsService = inject(InsightsService);
  private readonly notificationService = inject(ExportNotificationService);
  private readonly translate = inject(TranslateService);
  private readonly installationGroupStore = inject(InstallationGroupStore);
  private readonly destroyRef = inject(DestroyRef);

  private readonly allJobs = signal<ExportJobCardState[]>([]);
  private readonly notifiedJobIds = new Set<string>();
  /** Energy preset per job, remembered client-side at creation time. The backend does not
   *  return the preset on the job list yet, so this is the only source for the history "Data"
   *  column; it is lost on reload, after which such jobs fall back to "all data". */
  private readonly presetByOid = new Map<string, EnergyPreset>();
  private pollSubscription: Subscription | null = null;
  private readonly currentPollInterval = signal(LAZY_POLL_MS);

  activeJobs = computed(() => this.allJobs().filter(j => ['queued', 'running'].includes(j.status)));
  completedJobs = computed(() =>
    this.allJobs()
      .filter(j => j.status === 'completed')
      .slice(0, MAX_HISTORY),
  );
  failedJobs = computed(() =>
    this.allJobs()
      .filter(j => j.status === 'failed')
      .slice(0, MAX_FAILED),
  );

  isPollingActive = signal(false);

  constructor() {
    effect(() => {
      const hasActive = this.activeJobs().length > 0;
      if (hasActive) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    });
  }

  /** Called by the page right after a job is created, so the history "Data" column can show
   *  the chosen preset before/without the backend echoing it back. */
  rememberPreset(jobOid: string, preset: EnergyPreset): void {
    this.presetByOid.set(jobOid, preset);
    // Backfill an already-loaded card (loadHistory may have run before this resolves).
    this.allJobs.update(jobs =>
      jobs.map(j => (j.jobOid === jobOid ? { ...j, energyPreset: preset } : j)),
    );
  }

  loadHistory(limit: number = 100, offset: number = 0): void {
    this.insightsService
      .listExportJobs(limit, offset)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const cardStates = response.jobs.map(job => this.toCardState(job));
        this.allJobs.set(cardStates);
        // Jobs already completed/failed at load time are history, not a fresh transition — don't toast them.
        this.markExistingJobsAsNotified(response.jobs);
      });
  }

  private markExistingJobsAsNotified(jobs: ExportJob[]): void {
    jobs.forEach(job => {
      if (job.status === 'completed' || job.status === 'failed') {
        this.notifiedJobIds.add(job.export_job_oid);
      }
    });
  }

  addJobsToTracking(jobs: ExportJobCardState[]): void {
    const current = this.allJobs();
    const combined = [
      ...jobs,
      ...current.filter(c => !jobs.some(j => j.jobOid === c.jobOid)),
    ];
    this.allJobs.set(combined);
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.isPollingActive.set(true);
    this.poll();
  }

  /** Fetches immediately, then reschedules itself at whatever cadence the fresh job statuses call for. */
  private poll(): void {
    const oids = this.activeJobs().map(j => j.jobOid);
    this.pollSubscription = this.insightsService
      .listExportJobs(100, 0, oids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const cardStates = response.jobs.map(job => this.toCardState(job));
          this.mergeJobUpdates(cardStates);
          this.checkAndNotifyCompletedJobs(response.jobs);
          this.pruneJobHistory();
          this.scheduleNextPoll();
        },
        error: () => {
          this.scheduleNextPoll();
        },
      });
  }

  private scheduleNextPoll(): void {
    // Check activeJobs() directly rather than isPollingActive(): the latter is only flipped off
    // by the constructor's effect(), which reacts to activeJobs() asynchronously. Relying on it
    // here would schedule one extra poll after the last job goes terminal, in the window before
    // that effect runs.
    if (!this.isPollingActive() || !this.activeJobs().length) return;
    this.updatePollInterval();
    this.pollSubscription = timer(this.currentPollInterval())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.poll());
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
    this.isPollingActive.set(false);
  }

  private updatePollInterval(): void {
    const jobs = this.activeJobs();
    const hasRunning = jobs.some(j => j.status === 'running');
    const hasQueued = jobs.some(j => j.status === 'queued');

    let newInterval = LAZY_POLL_MS;
    if (hasRunning) {
      newInterval = AGGRESSIVE_POLL_MS;
    } else if (hasQueued) {
      newInterval = STANDARD_POLL_MS;
    }

    this.currentPollInterval.set(newInterval);
  }

  private mergeJobUpdates(updatedJobs: ExportJobCardState[]): void {
    const current = this.allJobs();
    const merged = current.map(existing => {
      const updated = updatedJobs.find(u => u.jobOid === existing.jobOid);
      return updated || existing;
    });

    updatedJobs.forEach(job => {
      if (!merged.some(m => m.jobOid === job.jobOid)) {
        merged.push(job);
      }
    });

    this.allJobs.set(merged);
  }

  private checkAndNotifyCompletedJobs(jobs: ExportJob[]): void {
    jobs.forEach(job => {
      const isCompleted = job.status === 'completed' || job.status === 'failed';

      if (isCompleted && !this.notifiedJobIds.has(job.export_job_oid)) {
        this.notifiedJobIds.add(job.export_job_oid);

        if (job.status === 'completed') {
          this.notificationService.add({
            jobOid: job.export_job_oid,
            type: 'success',
            title: this.translate.instant('dataExport.history.statusReady'),
            message: this.translate.instant('dataExport.exportCompleted', { devices: this.getJobLabel(job) }),
            primaryAction: {
              label: this.translate.instant('dataExport.download'),
              handler: async () => {
                await this.downloadJob(job.export_job_oid);
              },
            },
            sticky: true,
            position: 'top-right',
          });
        } else if (job.status === 'failed') {
          // No retry action — user starts a fresh export from the form instead.
          this.notificationService.add({
            jobOid: job.export_job_oid,
            type: 'error',
            title: this.translate.instant('dataExport.history.statusFailed'),
            message: this.translate.instant('dataExport.exportFailed', { devices: this.getJobLabel(job) }),
            errorDetails: job.error_message || undefined,
            sticky: true,
            position: 'top-right',
          });
        }
      }
    });
  }

  private pruneJobHistory(): void {
    const current = this.allJobs();

    const completed = current.filter(j => j.status === 'completed').slice(0, MAX_HISTORY);
    const failed = current.filter(j => j.status === 'failed').slice(0, MAX_FAILED);
    const active = current.filter(j => ['queued', 'running'].includes(j.status));

    const pruned = [...active, ...completed, ...failed];
    if (pruned.length !== current.length) {
      this.allJobs.set(pruned);
    }
  }

  private toCardState(job: ExportJob): ExportJobCardState {
    const progress = this.insightsService.calculateProgress(job);

    const startedTime = job.started_at ? new Date(job.started_at).getTime() : null;
    const elapsedSeconds = startedTime ? (Date.now() - startedTime) / 1000 : 0;
    let estimatedRemaining: number | null = null;

    if (job.estimated_duration_seconds && elapsedSeconds < job.estimated_duration_seconds) {
      estimatedRemaining = job.estimated_duration_seconds - elapsedSeconds;
    }

    return {
      jobOid: job.export_job_oid,
      status: job.status,
      progress,
      estimatedSecondsRemaining: estimatedRemaining,
      deviceLabel: this.getJobLabel(job),
      deviceCount: job.device_count,
      processedDataPoints: job.processed_data_points || 0,
      totalDataPoints: job.total_data_points || 0,
      fileSizeMb: job.file_size_mb || null,
      errorMessage: job.error_message || null,
      createdAt: new Date(job.created_at),
      startedAt: job.started_at ? new Date(job.started_at) : null,
      downloadUrl: job.download_url || null,
      startDate: job.start_date,
      endDate: job.end_date,
      granularity: job.granularity,
      energyPreset: this.presetByOid.get(job.export_job_oid) ?? job.energy_preset ?? null,
      fileName: buildExportFileName(job.granularity, new Date(job.created_at)),
    };
  }

  private async downloadJob(jobOid: string): Promise<void> {
    const response = await firstValueFrom(this.insightsService.downloadExportJob(jobOid));
    if (!response.body) return;
    // Server returns a gzipped CSV; decompress so the user gets a plain .csv file.
    // Prefer the `Export_{creation-date}_{granularity}_values.csv` name; fall back to the server's.
    const card = this.allJobs().find(j => j.jobOid === jobOid);
    const contentDisposition = response.headers.get('content-disposition') ?? '';
    const rawName = parseContentDispositionFilename(contentDisposition) || 'export.csv.gz';
    const filename = card?.fileName || rawName.replace(/\.gz$/i, '');
    const csvBlob = await gunzipCsvUtil.gunzipToCsvBlob(response.body);
    gunzipCsvUtil.triggerBlobDownload(csvBlob, filename);
  }

  private getJobLabel(job: ExportJob): string {
    if (job.installation_group_oid) {
      const name = this.installationGroupStore
        .data()
        ?.installationGroups.find(g => g.installationGroupOid === job.installation_group_oid)?.name;
      return this.translate.instant('dataExport.groupChipLabel', { name: name ?? job.installation_group_oid });
    }
    if (job.device_count === 1 && job.device_ldn) {
      return job.device_ldn;
    }
    return job.device_count === 1
      ? this.translate.instant('dataExport.history.oneDevice')
      : this.translate.instant('dataExport.history.deviceCount', { count: job.device_count });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
